import React, { useState, useEffect } from 'react';
import { Base_URL, imageBaseURL } from '../../actions/Base_URL';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import './AllEvent.css';

const AllEvent = () => {
  const [events, setEvents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${Base_URL}/events/getAll`);
        const data = await response.json();

        if (data.success) {
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Include events starting today
          const validEvents = (data.data || []).filter(e => {
            const startDate = new Date(e.start_date);
            return startDate >= today;
          });
          setEvents(validEvents);
        } else if (data.message === 'Records not found') {
          setEvents([]);
        } else {
          setError('Failed to fetch events');
        }
      } catch (err) {
        setError('Error fetching events: ' + err.message);
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Handle responsive slides to show
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSlidesToShow(1);
      } else if (window.innerWidth <= 1100) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (events.length === 0 || events.length <= slidesToShow) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev >= events.length - slidesToShow ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [events.length, slidesToShow]);

  const handleNextClick = () => {
    if (events.length <= slidesToShow) return;
    setCurrentSlide((prev) =>
      prev >= events.length - slidesToShow ? 0 : prev + 1
    );
  };

  const handlePrevClick = () => {
    if (events.length <= slidesToShow) return;
    setCurrentSlide((prev) =>
      prev === 0 ? events.length - slidesToShow : prev - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Modal handlers
  const openModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  if (loading) {
    return (
      <div className="state-container">
        <div className="state-content loading">
          <div className="state-text">Loading Events...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="state-container">
        <div className="state-content error">
          <div className="state-text error-text">{error}</div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full min-h-[300px] py-12 px-4">
        <div className="bg-gray-100 p-6 rounded-full shadow-sm mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-400"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
            <line x1="9" y1="16" x2="15" y2="16"></line>
            <line x1="12" y1="13" x2="12" y2="19"></line>
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Upcoming Events</h3>
        <p className="text-gray-500 text-center max-w-sm">
          We don't have any events scheduled at the moment. Check back soon for exciting updates!
        </p>
      </div>
    );
  }

  const maxDots = events.length > slidesToShow ? events.length - slidesToShow + 1 : 1;

  return (
    <section className="all-event-container">
      <div className="all-event-wrapper">
        {/* Header Section */}
        <div className="all-event-header">
          <h2 className="all-event-subtitle">Discover & Participate</h2>
          <h3 className="all-event-title">Upcoming Events</h3>
        </div>

        <div className="carousel-outer-wrapper">
          <div className="carousel-inner-container">
            {/* Navigation Arrows */}
            {events.length > slidesToShow && (
              <>
                <button
                  className="carousel-nav-btn prev"
                  onClick={handlePrevClick}
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className="carousel-nav-btn next"
                  onClick={handleNextClick}
                  aria-label="Next slide"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            <div
              className="events-track"
              style={{
                transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
                transition: "transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
                display: 'flex',
                gap: '30px',
                width: '100%'
              }}
            >
              {events.map((event, index) => (
                <div
                  className="fancybox-item"
                  key={`${event.event_id}-${index}`}
                  style={{
                    flex: `0 0 calc(${100 / slidesToShow}% - ${((slidesToShow - 1) * 30) / slidesToShow}px)`,
                    boxSizing: 'border-box',
                    width: 'auto' // Override fixed width
                  }}
                >
                  <h4 className="fancybox-title-top">{event.event_name}</h4>
                  <div className="fancybox-img">
                    {event.event_images && event.event_images.length > 0 ? (
                      <>
                        <img
                          src={`${imageBaseURL}${event.event_images[0]}`}
                          alt={event.event_name}
                        />
                        <div className="image-gradient-overlay"></div>
                      </>
                    ) : (
                      <div className="event-image-placeholder">Event Preview</div>
                    )}
                  </div>
                  <div className="fancybox-content">
                    <div className="event-schedule-info">
                      <div className="schedule-badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <span className="schedule-date">
                          {formatDate(event.start_date)} - {formatDate(event.end_date)}
                        </span>
                      </div>
                    </div>

                    <p className="fancybox-desc">
                      {event.description ||
                        "Join us for this exciting learning experience and expand your knowledge."}
                    </p>
                    <button onClick={() => openModal(event)} className="btn-link">
                      <span>Read More</span>
                      <div className="arrow-circle">
                        <ChevronRight size={16} color="white" />
                      </div>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Navigation */}
          {events.length > slidesToShow && (
            <div className="carousel-dots">
              {Array.from({ length: maxDots }).map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${currentSlide === index ? "active" : ""}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-16 mx-auto max-w-5xl px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-dark-tertiary to-blue-900/20 border border-white/10 p-8 md:p-12 text-center shadow-2xl group hover:border-blue-500/30 transition-all duration-300">
            {/* Decorative background blobs */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none"></div>

            <h3 className="text-2xl md:text-4xl font-extrabold text-white mb-6 relative z-10 tracking-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Exciting opportunities</span> await you at NVCTI
            </h3>
            <p className="text-gray-400 text-lg mb-8 relative z-10 max-w-2xl mx-auto leading-relaxed">
              Dive into a world of innovation, workshops, and competitions. Join our community and turn your ideas into reality. Don't miss out on what's happening next! <span className="text-blue-400 font-bold">Discover more events and participate!</span>
            </p>


          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {isModalOpen && selectedEvent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{selectedEvent.event_name}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {selectedEvent.event_images && selectedEvent.event_images.length > 0 && (
                <img
                  src={`${imageBaseURL}${selectedEvent.event_images[0]}`}
                  alt={selectedEvent.event_name}
                  className="modal-image"
                />
              )}

              <div className="modal-info-row">
                <div className="modal-info-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div className="modal-info-text">
                  {formatDate(selectedEvent.start_date)} - {formatDate(selectedEvent.end_date)}
                </div>
              </div>

              <div className="modal-description">
                {selectedEvent.description}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AllEvent;
